import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MinusIcon, PlusIcon, Trash2Icon, CheckIcon } from 'lucide-react';
import { formatPrice } from '../data/smaken';
import { useShop } from '../ShopContext';
import { Reveal } from '../components/Reveal';
import { ArrowMark, Note } from '../components/Marks';
const SHIPPING_COST = 79;
export function ShopCheckout() {
  const {
    lines,
    setQuantity,
    remove,
    total,
    clear
  } = useShop();
  const [buyer, setBuyer] = useState<'privat' | 'foretag'>('privat');
  const [payment, setPayment] = useState<'kort' | 'faktura'>('kort');
  const [recurring, setRecurring] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    org: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const set = (key: keyof typeof form, value: string) => setForm(p => ({
    ...p,
    [key]: value
  }));
  const needsShipping = lines.some(l => l.shipping === 'frakt');
  const shipping = needsShipping && total < 800 ? SHIPPING_COST : 0;
  const emailValid = /\S+@\S+\.\S+/.test(form.email);
  const valid = Boolean(form.name.trim() && form.address.trim()) && emailValid;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setOrderNumber(`SMÅ-${String(Date.now()).slice(-6)}`);
    clear();
  };
  if (orderNumber) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1305" data-mp="p7euz2w7iprm">
        <Reveal data-id="element-1306" data-mp="p5lnjldrs1ik">
          <CheckIcon className="mx-auto h-12 w-12 text-blaa" aria-hidden="true" data-id="element-1307" data-mp="tywxvwhsj0f8" />
          <h1 className="display-xl mt-6 text-4xl uppercase sm:text-6xl" data-id="element-1308" data-mp="u111edbs1xez">Beställt.</h1>
          <p className="mt-4 text-lg leading-relaxed text-black/70" data-id="element-1309" data-mp="yzuazuny149v">
            Kvitto och leveransbesked är på väg till din mejl. Vi packar allt för hand, så det tar en dag
            eller två innan det går iväg.
          </p>
          <div className="mt-8 border-2 border-black px-6 py-5" data-id="element-1310" data-mp="341b01wac7ilu">
            <p className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1311" data-mp="33zhvqnn75ah0">Ordernummer</p>
            <p className="display-xl mt-1 text-2xl" data-id="element-1312" data-mp="2zxwdemrw83zf">{orderNumber}</p>
          </div>
          <Link to="/butik" className="mt-8 inline-block bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-blaa" data-id="element-1313" data-mp="3dsoye0qjycde">
            Tillbaka till butiken
          </Link>
        </Reveal>
      </div>;
  }
  if (lines.length === 0) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1314" data-mp="l3ypwfe9xg8">
        <h1 className="display-xl text-4xl uppercase sm:text-6xl" data-id="element-1315" data-mp="3rr5cwdunp763">Tom korg.</h1>
        <Note className="mt-4 block" data-id="element-1316" data-mp="3rp2htfencnui">Det finns gott att fylla den med.</Note>
        <Link to="/butik" className="mt-8 inline-block bg-blaa px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1317" data-mp="22cqs24mre4fv">
          Till butiken
        </Link>
      </div>;
  }
  const inputClass = (invalid: boolean) => `w-full border-b bg-transparent py-3 font-display text-xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-blaa sm:text-2xl ${invalid ? 'border-rod' : 'border-black/25'}`;
  const labelClass = 'text-[11px] font-bold uppercase tracking-wide2 text-black/45';
  const choice = (active: boolean) => `border-2 px-6 py-4 text-left transition-colors duration-150 ease-out ${active ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`;
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-1318" data-mp="3lades8se706p">
      <Reveal data-id="element-1319" data-mp="3lcq0mx19tup1">
        <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-1320" data-mp="1fi80mqhjyned">Kassa</h1>
        <p className="mt-3 text-lg text-black/60" data-id="element-1321" data-mp="1fg3x49nkfqem">Betala nu, packat för hand inom två dagar.</p>
      </Reveal>

      <div className="mt-12 grid gap-14 lg:grid-cols-[minmax(0,1.3fr),minmax(0,1fr)] lg:gap-20" data-id="element-1322" data-mp="usvvvdnbgjx2">
        <form onSubmit={submit} className="space-y-10" data-id="element-1323" data-mp="ptt24pcse04e">
          <section data-id="element-1324" data-mp="pucb3gkgle6a">
            <h2 className={labelClass} data-id="element-1325" data-mp="prq1zchx6qly">Vem handlar?</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1326" data-mp="ktokux4211d4">
              <button type="button" onClick={() => setBuyer('privat')} className={choice(buyer === 'privat')} data-id="element-1327" data-mp="fvn1eb2pv014">
                <p className="font-display text-2xl uppercase" data-id="element-1328" data-mp="btrtgnqa5oed">Privat</p>
                <p className="mt-1 text-sm opacity-70" data-id="element-1329" data-mp="bvur9txocltn">Hem till dörren</p>
              </button>
              <button type="button" onClick={() => setBuyer('foretag')} className={choice(buyer === 'foretag')} data-id="element-1330" data-mp="718gnszf2xam">
                <p className="font-display text-2xl uppercase" data-id="element-1331" data-mp="3tc2vtfwctqhw">Företag</p>
                <p className="mt-1 text-sm opacity-70" data-id="element-1332" data-mp="3oadh9q9xaup2">Faktura och stående order</p>
              </button>
            </div>
          </section>

          <section className="space-y-8" data-id="element-1333" data-mp="3kbxy5xgyq6gx">
            <div data-id="element-1334" data-mp="1zsc4lm7ioaf5">
              <label htmlFor="s-name" className={labelClass} data-id="element-1335" data-mp="1zqr9nvhaipgr">
                {buyer === 'foretag' ? 'Företagsnamn' : 'Namn'} *
              </label>
              <input id="s-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder={buyer === 'foretag' ? 'Verket AB' : 'För- och efternamn'} className={inputClass(showErrors && !form.name.trim())} data-id="element-1336" data-mp="28sh99olbsupm" />
            </div>

            {buyer === 'foretag' && <div data-id="element-1337" data-mp="1l367q4w5iwaf">
                <label htmlFor="s-org" className={labelClass} data-id="element-1338" data-mp="1q1zjnsvsif8h">
                  Organisationsnummer
                </label>
                <input id="s-org" value={form.org} onChange={e => set('org', e.target.value)} placeholder="556123-4567" className={inputClass(false)} data-id="element-1339" data-mp="1ut7v0ugokaoa" />
              </div>}

            <div className="grid gap-8 sm:grid-cols-2" data-id="element-1340" data-mp="ub7axd6rmziq">
              <div data-id="element-1341" data-mp="u9w577trfhoz">
                <label htmlFor="s-email" className={labelClass} data-id="element-1342" data-mp="qa3w8u0gf9j3">
                  E-post *
                </label>
                <input id="s-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="namn@mail.se" className={inputClass(showErrors && !emailValid)} data-id="element-1343" data-mp="1418x1zfk67v0" />
              </div>
              <div data-id="element-1344" data-mp="7cktu98i4p5n">
                <label htmlFor="s-phone" className={labelClass} data-id="element-1345" data-mp="7d2s1beq5dg7">
                  Telefon
                </label>
                <input id="s-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="070 123 45 67" className={inputClass(false)} data-id="element-1346" data-mp="l8obl326v518" />
              </div>
            </div>

            <div data-id="element-1347" data-mp="39o80h7d5b9xr">
              <label htmlFor="s-address" className={labelClass} data-id="element-1348" data-mp="39nonm52crbql">
                {needsShipping ? 'Leveransadress *' : 'Adress *'}
              </label>
              <input id="s-address" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Storgatan 1, 352 30 Växjö" className={inputClass(showErrors && !form.address.trim())} data-id="element-1349" data-mp="30o1ib86i5ls9" />
            </div>

            <div data-id="element-1350" data-mp="19fyj6z1h336">
              <label htmlFor="s-notes" className={labelClass} data-id="element-1351" data-mp="17v21bt5za2o">
                Meddelande eller gåvohälsning
              </label>
              <textarea id="s-notes" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Skriv en hälsning så lägger vi den i lådan." className={`${inputClass(false)} resize-none`} data-id="element-1352" data-mp="3jmnk6p9ufgx8" />
            </div>
          </section>

          <section data-id="element-1353" data-mp="2h13ine6d5ewn">
            <h2 className={labelClass} data-id="element-1354" data-mp="2h1mohwiqocub">Betalning</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1355" data-mp="2c9leyicyh07z">
              <button type="button" onClick={() => setPayment('kort')} className={choice(payment === 'kort')} data-id="element-1356" data-mp="2c78qrnglttmh">
                <p className="font-display text-2xl uppercase" data-id="element-1357" data-mp="277w350hwsiiu">Kort</p>
                <p className="mt-1 text-sm opacity-70" data-id="element-1358" data-mp="22cqlidvjhq0k">Dras direkt</p>
              </button>
              <button type="button" onClick={() => setPayment('faktura')} disabled={buyer !== 'foretag'} className={`${choice(payment === 'faktura')} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-1359" data-mp="2zxl4jeyj6kkz">
                <p className="font-display text-2xl uppercase" data-id="element-1360" data-mp="2ly3dsu1ocx8z">Faktura</p>
                <p className="mt-1 text-sm opacity-70" data-id="element-1361" data-mp="1elbggdcdoulm">30 dagar, endast företag</p>
              </button>
            </div>

            {buyer === 'foretag' && <label className="mt-5 flex cursor-pointer items-start gap-3 border-l-2 border-blaa pl-5" data-id="element-1362" data-mp="1am2l541owony">
                <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} className="mt-1 h-4 w-4 accent-blaa" data-id="element-1363" data-mp="3m58jvrzr2nut" />
                <span data-id="element-1364" data-mp="3tvj5o07w24u">
                  <span className="font-display text-xl" data-id="element-1365" data-mp="3udkv05nf8k5">Gör det här till en stående order</span>
                  <span className="mt-1 block text-sm leading-relaxed text-black/60" data-id="element-1366" data-mp="8rdkas7ztd2a">
                    Samma leverans varje vecka eller månad. Vi hör av oss för att bestämma intervall.
                  </span>
                </span>
              </label>}
          </section>

          {showErrors && !valid && <p role="alert" className="text-sm font-bold uppercase tracking-wide2 text-rod" data-id="element-1367" data-mp="mretqw2bpstd">
              Fyll i fälten märkta med *
            </p>}

          <button type="submit" className="group inline-flex items-center gap-3 bg-blaa px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1368" data-mp="2oirowtehomc8">
            Slutför köp
            <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1369" data-mp="1k6oc51mwp5ky" />
          </button>
        </form>

        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1370" data-mp="1p72m4p82iplm">
          <div className="border-2 border-black p-6" data-id="element-1371" data-mp="1t7mj9ssn5t7r">
            <h2 className="display-xl text-2xl uppercase" data-id="element-1372" data-mp="1t50chvdkrhql">Din korg</h2>

            <ul className="mt-6 border-t border-black/15" data-id="element-1373" data-mp="1y4uzvoy55p6w">
              {lines.map(line => <li key={line.id} className="border-b border-black/15 py-4" data-id="element-1374" data-mp="22y7ct1tgk7jt">
                  <div className="flex gap-4" data-id="element-1375" data-mp="27w8xtsag2gxx">
                    <img src={line.image} alt="" className="h-16 w-16 shrink-0 object-cover" data-id="element-1376" data-mp="27y22x35est3t" />
                    <div className="min-w-0 flex-1" data-id="element-1377" data-mp="2cszas6qycwf4">
                      <p className="font-display text-lg leading-tight" data-id="element-1378" data-mp="2gvw0siitnut3">{line.name}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1379" data-mp="59tsf8zjxihy">
                        {line.producer}
                      </p>
                    </div>
                    <button type="button" onClick={() => remove(line.id)} aria-label={`Ta bort ${line.name}`} className="shrink-0 text-black/35 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1380" data-mp="3sdrseu0rf9iu">
                      <Trash2Icon className="h-4 w-4" data-id="element-1381" data-mp="jxonux4y20yq" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3" data-id="element-1382" data-mp="f6q23i16sao3">
                    <div className="flex items-center border border-black/20" data-id="element-1383" data-mp="a8oo3bimb1jh">
                      <button type="button" onClick={() => setQuantity(line.id, line.quantity - 1)} aria-label="Minska" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1384" data-mp="a6kfwhdorgde">
                        <MinusIcon className="h-3.5 w-3.5" data-id="element-1385" data-mp="31603lax1vbsa" />
                      </button>
                      <span className="min-w-[2.5rem] text-center text-sm font-semibold" data-id="element-1386" data-mp="2nebn6vfrq15i">
                        {line.quantity}
                      </span>
                      <button type="button" onClick={() => setQuantity(line.id, line.quantity + 1)} aria-label="Öka" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1387" data-mp="2dh74j5l4sntg">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-1388" data-mp="29hg56dhjgzdb" />
                      </button>
                    </div>
                    <span className="font-display text-lg" data-id="element-1389" data-mp="7nyypd55a4k5">{formatPrice(line.price * line.quantity)}</span>
                  </div>
                </li>)}
            </ul>

            <div className="mt-5 space-y-1.5 text-sm" data-id="element-1390" data-mp="hpt86a7gzc7b">
              <div className="flex justify-between text-black/55" data-id="element-1391" data-mp="ctvq9zgk5xsf">
                <span data-id="element-1392" data-mp="qjz5w7kxmt8w">Varor</span>
                <span data-id="element-1393" data-mp="ql95x2bcibi6">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between text-black/55" data-id="element-1394" data-mp="ltr4ye9boax7">
                <span data-id="element-1395" data-mp="lreefcpi0s8j">Frakt</span>
                <span data-id="element-1396" data-mp="10i59quqau9ge">{shipping === 0 ? 'Ingår' : formatPrice(shipping)}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-black/15 pt-3" data-id="element-1397" data-mp="vjkezattghzc">
                <span className="text-[11px] font-bold uppercase tracking-wide2" data-id="element-1398" data-mp="19itl1hob5vpl">Att betala</span>
                <span className="display-xl text-2xl" data-id="element-1399" data-mp="15hrm71yazlo5">{formatPrice(total + shipping)}</span>
              </div>
            </div>

            {needsShipping && total < 800 && <Note className="mt-4 block !text-lg" data-id="element-1400" data-mp="3bc9nrcxo710x">
                Handla för {formatPrice(800 - total)} till så bjuder vi på frakten.
              </Note>}
          </div>
        </aside>
      </div>
    </div>;
}